import { Resp } from '@services/api.service';
import { isDate, parse, startOfDay } from 'date-fns';
import { deburr, isNil, lowerCase, padEnd, size, trim } from 'lodash';
import { MonoTypeOperatorFunction, Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

const iso8601Full = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/;
const iso8601Zulu =
    /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;
const iso8601Date = /^\d{4}-\d{2}-\d{2}/;

export function reduce(data: Object[]): Object {
    return data.reduce((acc, curr) => ({ ...acc, ...curr }), []);
}

export function someValue(obj: Object = {}): boolean {
    return !!Object.values(obj).some((v) => !!v);
}

export function safeEmptyList<T>() {
    return (source: Observable<Resp<T[]>>): Observable<T[]> =>
        source.pipe(
            catchError(() => of({ ret: 0, data: [] })),
            map((res) => (res.ret === 1 ? res.data : []))
        );
}

export function compareLike(x: any, y: any): boolean {
    return sanitize(y).indexOf(sanitize(x)) !== -1;
}

export function sanitize(data: string): string {
    return lowerCase(deburr(trim(data)));
}

export function notNull<T>(): MonoTypeOperatorFunction<T> {
    return filter((value) => !!value);
}

export function safeEmpty<T>(value: T = null) {
    return (source: Observable<Resp<T>>): Observable<T> =>
        source.pipe(
            catchError(() => of({ ret: 0, data: value })),
            map((res) => (res.ret === 1 ? res.data : value))
        );
}

export function sanitizeHtml(text: string): string {
    const stripped = text.replace(/<[^>]*>/g, '').trim();
    return decodeHtmlEntities(stripped);
}

export function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const newText = textarea.value;
    textarea.remove();
    return newText;
}

export function safeDate(date: any): Date | null {
    if (isNil(date)) {
        return null;
    }
    if (isDate(date)) {
        return date;
    }
    if (iso8601Full.test(date)) {
        return parse(date, 'yyyy-MM-dd HH:mm:ss', new Date());
    }
    if (iso8601Zulu.test(date)) {
        return new Date(date);
    }
    if (iso8601Date.test(date)) {
        return startOfDay(parse(date, 'yyyy-MM-dd', new Date()));
    }
    return date;
}

export function dynamicMask(
    value: string | null,
    mask: string,
    isHispano: boolean = false
): string {
    if (!value || !!isHispano) {
        return mask;
    }

    const digits = value.replace(/\D/g, '');

    if (digits.startsWith('0800')) {
        return '0000-000-0000';
    }

    if (digits.length === 8) {
        return '0000-00000';
    }

    if (digits.length === 10) {
        return '(00) 0000-00000';
    }

    if (digits.length === 11) {
        return '(00) 00000-00000';
    }

    return padEnd('', size(value) + 1, '0');
}
